/**
 * Anonymous-thread mapping API (INH-5360 / INH-5375).
 *
 * Replaces the `anonymous_thread_id` query parameter that used to hang off
 * get-metadata. That coupling meant a single stale thread ID failed the call
 * that also carries subscription state, which is how INH-5217 manifested.
 *
 * The endpoint answers 200 unless authentication fails, and reports every
 * submitted ID in exactly one bucket.
 */
import apiClient, { bearerAuthHeaders } from "src/services/apiClient";

export interface MapAnonymousResult {
  success: string[];
  already_mapped: string[];
  belongs_to_another_user: string[];
  invalid_thread_id: string[];
}

const EMPTY_RESULT: MapAnonymousResult = {
  success: [],
  already_mapped: [],
  belongs_to_another_user: [],
  invalid_thread_id: [],
};

/**
 * Every bucket is treated as optional: a key missing from the response is an
 * empty array, never a crash. The client reads these to decide what to delete
 * locally, so a defensive read here is cheaper than a broken auth flow.
 */
const normaliseResult = (data: Partial<MapAnonymousResult> | null | undefined): MapAnonymousResult => {
  const bucket = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

  return {
    success: bucket(data?.success),
    already_mapped: bucket(data?.already_mapped),
    belongs_to_another_user: bucket(data?.belongs_to_another_user),
    invalid_thread_id: bucket(data?.invalid_thread_id),
  };
};

/** Every ID named in the response, across all four buckets. All are terminal. */
export const allReportedIds = (result: MapAnonymousResult): string[] => [
  ...result.success,
  ...result.already_mapped,
  ...result.belongs_to_another_user,
  ...result.invalid_thread_id,
];

export const mapAnonymousThreads = async (
  threadIds: string[],
  token?: string | null,
): Promise<MapAnonymousResult> => {
  if (!threadIds || threadIds.length === 0) {
    return EMPTY_RESULT;
  }
  const response = await apiClient.post(
    "/api/thread/map-anonymous",
    { anonymous_thread_ids: threadIds },
    { headers: bearerAuthHeaders(token) },
  );
  return normaliseResult(response.data as Partial<MapAnonymousResult>);
};
